import React, { useState } from 'react';
import { Marker } from '@vis.gl/react-maplibre';
import { useMapZoom } from './MapBearingContext';
import { TrafficLight, PedestrianLight } from '../regulator/TrafficLight';
import { LightState } from '../../types/protocol';
import type { TrafficSignalNode } from '../../data/reguladores-placeholder';
import { useEditMode } from '../../store/editMode';
import { useSignalPositionOverridesDraft } from '../../store/signalPositionOverridesDraft';
import { useSignalLifecycleDraft } from '../../store/signalLifecycleDraft';
import { useOverridesVersion } from '../../store/overridesVersion';
import { signalPositionOverrides } from '../../data/overridesRuntime';
import { Trash2 } from 'lucide-react';

interface TrafficSignalDotProps {
  signal: TrafficSignalNode;
}

const DIRECTION_LABELS: Record<string, string> = {
  forward: 'sentido directo',
  backward: 'sentido inverso',
  both: 'ambos sentidos',
};

const TAG_LABELS: Record<string, string> = {
  highway: 'Tipo',
  'traffic_signals': 'Señal',
  'traffic_signals:direction': 'Dirección',
  crossing: 'Cruce',
  button_operated: 'Pulsador',
  tactile_paving: 'Pavimento táctil',
  'traffic_signals:sound': 'Señal acústica',
  'traffic_signals:vibration': 'Vibración',
  bicycle: 'Bicicleta',
};

// Cada <Marker> de MapLibre lleva su propio z-index inline, que crea un
// contexto de apilamiento propio — un z-index alto en el popup interno
// (más abajo) no puede "escapar" por encima de OTRO marker hermano cuyo
// z-index sea mayor. CabinetMarker usa 10/20 (sin seleccionar/seleccionado);
// aquí se usa 15/25 para que un semáforo sea siempre clicable y su popup
// se vea por encima, incluso si un regulador cercano lo tapa.
export const TrafficSignalDot = React.memo(function TrafficSignalDot({
  signal,
}: TrafficSignalDotProps) {
  const mapZoom = useMapZoom();
  const [open, setOpen] = useState(false);
  const editModeActive = useEditMode(s => s.active);
  useOverridesVersion(s => s.version); // re-render al guardar (ver overridesRuntime.ts)
  const hasPositionOverride = signalPositionOverrides.some(d => d.id === signal.id);
  const stageSignalPosition = useSignalPositionOverridesDraft(s => s.stage);
  const removeSignalPositionOverride = useSignalPositionOverridesDraft(s => s.remove);
  const positionSaving = useSignalPositionOverridesDraft(s => s.saving);
  const [enteringCoords, setEnteringCoords] = useState(false);
  const [latInput, setLatInput] = useState('');
  const [lngInput, setLngInput] = useState('');
  const [coordError, setCoordError] = useState('');
  const [saveError, setSaveError] = useState('');
  const stageRemoveSignal = useSignalLifecycleDraft(s => s.stageRemove);
  const lifecycleSaving = useSignalLifecycleDraft(s => s.saving);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const deleteSignal = async () => {
    setSaveError('');
    if (!(await stageRemoveSignal(signal.id))) {
      setSaveError('No se pudo eliminar — reintenta');
      return;
    }
    setConfirmingDelete(false);
    setOpen(false);
  };

  const removePosition = async () => {
    setSaveError('');
    if (!(await removeSignalPositionOverride(signal.id))) setSaveError('No se pudo quitar — reintenta');
  };

  const submitCoords = async () => {
    const lat = parseFloat(latInput);
    const lng = parseFloat(lngInput);
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      setCoordError('Formato inválido — usa punto decimal, ej. 41.452768');
      return;
    }
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      setCoordError('Fuera de rango — latitud [-90, 90], longitud [-180, 180]');
      return;
    }
    setCoordError('');
    if (!(await stageSignalPosition({ id: signal.id, position: [lng, lat] }))) {
      setCoordError('No se pudo guardar — reintenta');
      return;
    }
    setEnteringCoords(false);
  };

  const direction = signal.tags['traffic_signals:direction'];
  const isCrossing = signal.tags.crossing === 'traffic_signals';
  const tooltipDir = direction ? ` · ${DIRECTION_LABELS[direction] ?? direction}` : '';
  const tooltipText = `#${signal.id}${isCrossing ? ' (cruce)' : ''}${tooltipDir}`;

  const showDetailed = mapZoom >= 17;

  // En modo "asignar semáforo" (Modo Edición → Cablear grupos), el click
  // sobre el punto elige el semáforo candidato en vez de abrir su popup.
  const handleClick = (e: React.MouseEvent) => {
    const editMode = useEditMode.getState();
    if (editMode.pickingSignal) {
      e.stopPropagation();
      editMode.stopPickingSignal();
      editMode.setPendingSignalId(signal.id);
      return;
    }
    setOpen(o => !o);
  };

  // Tooltip + popup shared between both render modes
  const tooltip = (
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 whitespace-nowrap pointer-events-none opacity-0 group-hover/dot:opacity-100 transition-opacity z-40">
      <span className="text-[10px] font-mono bg-gray-900/90 text-gray-300 px-1.5 py-0.5 rounded shadow">
        {tooltipText}
      </span>
    </div>
  );

  const popup = open && (
    <div
      className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 bg-gray-900/95 backdrop-blur-md border border-gray-600/50 rounded-lg shadow-2xl overflow-hidden z-50"
    >
      <div className="px-3 py-2 border-b border-gray-700/50 flex items-center justify-between">
        <span className="text-[11px] font-semibold text-white">
          Semáforo #{signal.id}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(false); }}
          className="text-gray-500 hover:text-gray-300 text-xs cursor-pointer"
        >
          ✕
        </button>
      </div>
      <div className="px-3 py-2 space-y-1 max-h-48 overflow-y-auto">
        {Object.entries(signal.tags).map(([key, value]) => (
          <div key={key} className="flex justify-between text-[10px] gap-2">
            <span className="text-gray-400 truncate">{TAG_LABELS[key] ?? key}</span>
            <span className="text-white font-mono text-right">{value}</span>
          </div>
        ))}
      </div>
      <div className="px-3 py-1.5 text-[9px] text-gray-600 border-t border-gray-700/50">
        {signal.position[1].toFixed(6)}, {signal.position[0].toFixed(6)}
      </div>

      {editModeActive && (
        <div
          className="px-3 py-2 border-t border-gray-700/50 space-y-1.5"
          onClick={(e) => e.stopPropagation()}
        >
          {saveError && <div className="text-[9px] text-red-400">{saveError}</div>}

          {hasPositionOverride && !enteringCoords && (
            <div className="rounded border border-amber-700/40 bg-amber-900/20 p-1.5">
              <div className="text-[9px] text-amber-300">Corrección de campo guardada</div>
              <button
                onClick={removePosition}
                disabled={positionSaving}
                className="text-[9px] text-gray-400 hover:text-white underline mt-0.5 disabled:opacity-50 disabled:cursor-wait"
              >
                Volver a la posición OSM
              </button>
            </div>
          )}

          {!enteringCoords && (
            <button
              onClick={() => {
                setLatInput(signal.position[1].toFixed(6));
                setLngInput(signal.position[0].toFixed(6));
                setCoordError('');
                setEnteringCoords(true);
              }}
              className="w-full px-2 py-1.5 rounded text-[10px] font-medium bg-amber-700/30 text-amber-300
                         border border-amber-700/50 hover:bg-amber-700/50 transition-colors"
            >
              Coordenadas exactas
            </button>
          )}

          {enteringCoords && (
            <div className="space-y-1.5">
              <div>
                <label className="text-[9px] text-gray-500">Latitud</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={latInput}
                  onChange={e => setLatInput(e.target.value)}
                  className="w-full bg-gray-800 text-gray-100 font-mono text-[10px] px-1.5 py-1 rounded border border-gray-700 focus:border-amber-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-[9px] text-gray-500">Longitud</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={lngInput}
                  onChange={e => setLngInput(e.target.value)}
                  className="w-full bg-gray-800 text-gray-100 font-mono text-[10px] px-1.5 py-1 rounded border border-gray-700 focus:border-amber-500 focus:outline-none"
                />
              </div>
              {coordError && <div className="text-[9px] text-red-400">{coordError}</div>}
              <div className="flex gap-1.5">
                <button
                  onClick={() => setEnteringCoords(false)}
                  className="flex-1 px-2 py-1 rounded text-[10px] font-medium bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={submitCoords}
                  disabled={positionSaving}
                  className="flex-1 px-2 py-1 rounded text-[10px] font-medium bg-amber-600 text-white hover:bg-amber-500 transition-colors disabled:opacity-50 disabled:cursor-wait"
                >
                  {positionSaving ? 'Guardando…' : 'Usar'}
                </button>
              </div>
            </div>
          )}

          {/* Eliminar semáforo (ARS-05) */}
          {!enteringCoords && !confirmingDelete && (
            <button
              onClick={() => setConfirmingDelete(true)}
              className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-[10px] font-medium
                         bg-gray-800 text-red-400 border border-gray-700 hover:bg-red-900/20 hover:border-red-800 transition-colors"
            >
              <Trash2 className="w-3 h-3" />Eliminar semáforo
            </button>
          )}
          {confirmingDelete && (
            <div className="space-y-1.5">
              <div className="text-[9px] text-red-400">Se quita de inmediato. ¿Seguro?</div>
              <div className="flex gap-1.5">
                <button
                  onClick={() => setConfirmingDelete(false)}
                  className="flex-1 px-2 py-1 rounded text-[10px] font-medium bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={deleteSignal}
                  disabled={lifecycleSaving}
                  className="flex-1 px-2 py-1 rounded text-[10px] font-medium bg-red-700 text-white hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-wait"
                >
                  {lifecycleSaving ? 'Eliminando…' : 'Confirmar'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (showDetailed) {
    const scale = Math.min(2, Math.max(0.3, Math.pow(2, (mapZoom - 18.5) * 0.5)));

    return (
      <Marker
        longitude={signal.position[0]}
        latitude={signal.position[1]}
        anchor="bottom"
        style={{ zIndex: open ? 25 : 15 }}
      >
        <div className="relative group/dot flex flex-col items-center cursor-pointer" onClick={handleClick}>
          <div style={{ transform: `scale(${scale})`, transformOrigin: 'bottom center' }} className="flex flex-col items-center">
            {/* Semáforo sin regulador asignado (ver santaColomaStaticSignals):
                no hay dato MQTT que le corresponda, así que se dibuja apagado
                en vez de fabricar un verde que induciría a pensar que está
                operando. */}
            {isCrossing ? (
              <PedestrianLight red={LightState.OFF} green={LightState.OFF} size="sm" />
            ) : (
              <TrafficLight red={LightState.OFF} amber={LightState.OFF} green={LightState.OFF} size="sm" />
            )}
            <div className="w-[2px] h-3 bg-gray-500/70 mx-auto" />
            <div className="w-3 h-1 rounded-full bg-black/25 blur-[1px]" />
          </div>
          {tooltip}
          {popup}
        </div>
      </Marker>
    );
  }

  // Zoom < 17: simple dot
  const size = Math.min(14, Math.max(5, (mapZoom - 13) * 2.5 + 5));

  return (
    <Marker
      longitude={signal.position[0]}
      latitude={signal.position[1]}
      anchor="center"
      style={{ zIndex: open ? 25 : 15 }}
    >
      <div className="relative group/dot">
        <div
          onClick={handleClick}
          className="rounded-full cursor-pointer transition-all duration-150 hover:brightness-125"
          style={{
            width: size,
            height: size,
            backgroundColor: '#9ca3af',
            border: '1.5px solid #6b7280',
            boxShadow: '0 0 4px rgba(0,0,0,0.4)',
          }}
        />
        {tooltip}
        {popup}
      </div>
    </Marker>
  );
});
